// constants
import {
    GRATIFICATION_HALF_CARD_LOTTIE,
    GRATIFICATION_HALF_CARD_LOTTIE_ASSETS,
    GRATIFICATION_HALF_CARD_LOTTIE_LOGO,
    GRATIFICATION_POPUP_LOTTIE,
    GRATIFICATION_POPUP_LOTTIE_ASSETS,
    GRATIFICATION_POPUP_LOTTIE_LAYER_NAMES,
    HALF_CARD_LOTTIE_IMAGES_ASSET_COUNT,
    PLAN_REMOVAL_HALF_CARD,
} from "../constants";

// utils
import { captureError, fetchLottieAsset, safeJsonParse } from "@swiggy-private/core-ui";
import { format, isEmpty } from "@swiggy-private/js-utils";

// types
import { LottieLayer, LOTTIE_ERROR_TYPES, OnLottieError } from "@swiggy-private/core-types";
import { GratificationPopupLottieData, PlanRemovalCardLottieData } from "../types";
import {
    ILottiePlanRemovalHalfCard,
    ILottiePlayerGratificationPopup,
} from "../Components/PostState/components/LottiePlayer/types";
import { ProgramSubTypes } from "@super-ui/interfaces/SwiggyOne";

export const isTextLayer = (layer: LottieLayer) => layer.ty == 5;

export const isPlanAddedLayer = (layer: LottieLayer) =>
    isTextLayer(layer) && layer.nm === GRATIFICATION_POPUP_LOTTIE_LAYER_NAMES.PLAN_ADDED;

export const isPlanNotAddedLayer = (layer: LottieLayer) =>
    isTextLayer(layer) && layer.nm === GRATIFICATION_POPUP_LOTTIE_LAYER_NAMES.PLAN_NOT_ADDED;

export const isWithoutTextLayer = (layer: LottieLayer) =>
    isTextLayer(layer) && layer.nm === GRATIFICATION_POPUP_LOTTIE_LAYER_NAMES.WITHOUT_TEXT;

export const isHeadingLayer = (layer: LottieLayer) =>
    isTextLayer(layer) && layer.nm === GRATIFICATION_POPUP_LOTTIE_LAYER_NAMES.HEADING;

export const isBillSubtextLayer = (layer: LottieLayer) =>
    isTextLayer(layer) && layer.nm === GRATIFICATION_POPUP_LOTTIE_LAYER_NAMES.BILL_SUBTEXT;

export const getLottie = (isGratificationPopup?: boolean, isPlanRemovalHalfCard?: boolean) => {
    if (isGratificationPopup) {
        return GRATIFICATION_POPUP_LOTTIE;
    } else if (isPlanRemovalHalfCard) {
        return GRATIFICATION_HALF_CARD_LOTTIE;
    } else {
        throw new Error("Error: Unreachable state");
    }
};

export const fetchLottie = async ({
    isPlanRemovalHalfCard,
    isGratificationPopup,
    onError,
}: {
    isPlanRemovalHalfCard?: boolean;
    isGratificationPopup?: boolean;
    onError: OnLottieError;
}) => {
    try {
        const lottie = getLottie(isGratificationPopup, isPlanRemovalHalfCard);
        const lottieRequest = await fetch(lottie ?? "");
        const lottieData = await lottieRequest.json();

        return lottieData;
    } catch (e) {
        captureError(e);
        onError(LOTTIE_ERROR_TYPES.FETCH_FAILED, e);
    }
};

// can't type lottie data
export const modifyGratifictionPopupLottie = async (
    onError: OnLottieError,
    lottieData: any,
    gratificationPopupData: GratificationPopupLottieData,
) => {
    try {
        const updatedLottie = (await safeJsonParse(JSON.stringify(lottieData))) as any; //can't type lottie data
        const { updatedAmount, previousAmount, heading, logo, billSubtext } = gratificationPopupData;

        updatedLottie.layers.forEach((layer: { t: any; ty: number; nm: string | string[] }) => {
            if (isPlanAddedLayer(layer)) {
                layer.t.d.k[0].s.t = `₹${updatedAmount}`;
            } else if (isPlanNotAddedLayer(layer)) {
                layer.t.d.k[0].s.t = `₹${previousAmount}`;
            } else if (isHeadingLayer(layer)) {
                layer.t.d.k[0].s.t = `${heading}`;
            } else if (isBillSubtextLayer(layer)) {
                layer.t.d.k[0].s.t = `${billSubtext}`;
            }
        });

        // update logo
        if (!isEmpty(logo) && updatedLottie?.assets?.[0]?.p && updatedLottie?.assets?.[0].u) {
            const logoAsset = await fetchLottieAsset(logo, onError);
            updatedLottie.assets[0].p = logoAsset;
            updatedLottie.assets[0].u = "";
        }

        // update static header asset
        if (updatedLottie?.assets?.[1]?.p && updatedLottie?.assets?.[1]?.u) {
            const staticAsset = await fetchLottieAsset(GRATIFICATION_POPUP_LOTTIE_ASSETS.STATIC_HEADER_ASSET, onError);
            updatedLottie.assets[1].p = staticAsset;
            updatedLottie.assets[1].u = "";
        }

        return updatedLottie;
    } catch (e) {
        captureError(e);
        onError(LOTTIE_ERROR_TYPES.FETCH_FAILED, e);
    }
};

// can't type lottie data
export const modifyPlanRemovalHalfCardLottie = async (
    onError: OnLottieError,
    lottieData: any,
    planRemovalCardData: PlanRemovalCardLottieData,
) => {
    try {
        const lottieStr = JSON.stringify(lottieData);
        const updatedLottie = (await safeJsonParse(lottieStr)) as any;

        for (let i = 0; i <= HALF_CARD_LOTTIE_IMAGES_ASSET_COUNT; i++) {
            let staticAsset = await fetchLottieAsset(format(GRATIFICATION_HALF_CARD_LOTTIE_ASSETS, i), onError);
            if (i === 1 && planRemovalCardData.planType === ProgramSubTypes.LITE) {
                staticAsset = await fetchLottieAsset(GRATIFICATION_HALF_CARD_LOTTIE_LOGO.ONE_LITE, onError);
            }
            if (i === 1 && planRemovalCardData.planType === ProgramSubTypes.PLUS) {
                staticAsset = await fetchLottieAsset(GRATIFICATION_HALF_CARD_LOTTIE_LOGO.ONE, onError);
            }
            updatedLottie.assets[i].p = staticAsset;
            updatedLottie.assets[i].u = "";
        }

        updatedLottie.layers.forEach((layer: { t: any; ty: number; nm: string | string[]; ef: any[] }) => {
            if (isPlanAddedLayer(layer)) {
                layer.t.d.k[0].s.t = `₹${planRemovalCardData?.updatedAmount}`;
            } else if (isPlanNotAddedLayer(layer)) {
                layer.t.d.k[0].s.t = `₹${planRemovalCardData?.previousAmount}`;
            }
            if (isWithoutTextLayer(layer) && planRemovalCardData.planType === ProgramSubTypes.PLUS) {
                layer.t.d.k[0].s.t = PLAN_REMOVAL_HALF_CARD;
            }
        });

        return updatedLottie;
    } catch (_err) {
        captureError(_err);
        onError();
    }
};

export const modifyLottie = async (
    props: {
        onError: OnLottieError;
        lottieData: any; // can't type lottie data
    } & (ILottiePlanRemovalHalfCard | ILottiePlayerGratificationPopup),
) => {
    const { onError, lottieData } = props;
    try {
        if ("isGratificationPopup" in props && props.isGratificationPopup) {
            return await modifyGratifictionPopupLottie(onError, lottieData, props.gratificationPopUpData);
        } else if ("isPlanRemovalHalfCard" in props && props.isPlanRemovalHalfCard) {
            return await modifyPlanRemovalHalfCardLottie(onError, lottieData, props.planRemovalHalfCardData);
        }
        return lottieData;
    } catch (_err) {
        onError();
    }
};
